import {
	BaseCanvasObject,
	CanvasObjectInterface,
} from 'canvas/base-canvas-object';
import { Selection } from 'canvas/selection';

import { LayerInterface } from 'types/layer';
import { COLORS } from 'constants/colors';

export class Layer
	extends BaseCanvasObject<BaseDrawOptions>
	implements LayerInterface
{
	private active = false;
	private children: Array<CanvasObjectInterface<any>> = [];
	private id: number | null = null;
	private savedState: {
		options: BaseDrawOptions;
		children: Record<string, any>;
	} = { options: { scale: 0, x: 0, y: 0, w: 0, h: 0 }, children: {} };
	private tags: string[] = [];
	private needToRender = true;
	private animationStatus: string = 'stopped';

	constructor(options: BaseDrawOptions) {
		super(options);
		this.setType('layer');
		this.children.push(
			new Selection({
				...options,
				lineWidth: 2,
				color: COLORS.SELECTION,
				scale: 1,
			})
		);
	}

	getAnimationStatus(): string {
		return this.animationStatus;
	}

	setAnimationStatus(status: string) {
		this.animationStatus = status;
	}

	addTag(tag: string) {
		this.tags.push(tag);
	}

	getTags(): string[] {
		return this.tags;
	}

	save() {
		this.savedState = {
			options: this.getOptions(),
			children: {},
		};

		for (const child of this.children) {
			this.savedState.children[child.getType()] = child.getOptions();
		}
	}

	restore() {
		if (this.savedState) {
			this.setOptions(this.savedState.options);

			for (const child of this.children) {
				const type = child.getType();
				if (this.savedState.children[type]) {
					child.setOptions({
						...(this.savedState.children[type] as object),
					});
				}
			}
		}
	}

	setActive(state: boolean) {
		this.active = state;
	}

	isActive() {
		return this.active;
	}

	setId(id: number) {
		this.id = id;
	}

	getId(): number | null {
		return this.id;
	}

	addChild(child: CanvasObjectInterface<any>) {
		this.children.push(child);
	}

	getChildren(): CanvasObjectInterface<any>[] {
		return this.children;
	}

	move(movementX: number, movementY: number) {
		const { x, y } = this.getOptions();
		const layerX = x + movementX;
		const layerY = y + movementY;

		this.setXY(layerX, layerY);
		this.moveChildrenAccordingly(movementX, movementY);
	}

	resize(movementX: number, movementY: number, direction = 'right') {
		if (['top-left', 'top', 'left'].includes(direction)) {
			this.resizeTopLeft(movementX, movementY);
		}

		if (direction === 'top-right') {
			this.resizeTopRight(movementX, movementY);
		}

		if (direction === 'bottom-left') {
			this.resizeBottomLeft(movementX, movementY);
		}

		if (['bottom-right', 'right', 'bottom'].includes(direction)) {
			this.resizeBottomRight(movementX, movementY);
		}
	}

	resizeBottomRight(movementX: number, movementY: number) {
		const { w, h } = this.getOptions();
		const delta = movementX + movementY;
		const movement = (Math.abs(movementX) + Math.abs(movementY)) / 2;

		if (delta < 0) {
			this.setWidthHeight(w - movement, h - movement);
			this.resizeChildrenAccordingly(-movement, -movement);
		} else {
			this.setWidthHeight(w + movement, h + movement);
			this.resizeChildrenAccordingly(movement, movement);
		}
	}

	resizeBottomLeft(movementX: number, movementY: number) {
		const { w, h } = this.getOptions();
		const delta = movementX;
		const movement = (Math.abs(movementX) + Math.abs(movementY)) / 2;

		const minDimension = this.getMinDimension();

		if (delta < 0) {
			this.move(-movement, 0);
			this.setWidthHeight(w + movement, h + movement);
			this.resizeChildrenAccordingly(movement, movement);
		} else {
			if (w - movement > minDimension && h - movement > minDimension) {
				this.move(movement, 0);
				this.setWidthHeight(w - movement, h - movement);
				this.resizeChildrenAccordingly(-movement, -movement);
			}
		}
	}

	resizeTopRight(movementX: number, movementY: number) {
		const { w, h } = this.getOptions();
		const delta = movementY;
		const movement = (Math.abs(movementX) + Math.abs(movementY)) / 2;

		const minDimension = this.getMinDimension();

		if (delta < 0) {
			this.move(0, -movement);
			this.setWidthHeight(w + movement, h + movement);
			this.resizeChildrenAccordingly(movement, movement);
		} else {
			if (w - movement > minDimension && h - movement > minDimension) {
				this.move(0, movement);
				this.setWidthHeight(w - movement, h - movement);
				this.resizeChildrenAccordingly(-movement, -movement);
			}
		}
	}

	resizeTopLeft(movementX: number, movementY: number) {
		const { w, h } = this.getOptions();
		const delta = movementX + movementY;
		const movement = (Math.abs(movementX) + Math.abs(movementY)) / 2;

		const minDimension = this.getMinDimension();

		if (delta < 0) {
			this.move(-movement, -movement);
			this.resizeChildrenAccordingly(movement, movement);
			this.setWidthHeight(w + movement, h + movement);
		} else {
			if (w - movement > minDimension && h - movement > minDimension) {
				this.move(movement, movement);
				this.resizeChildrenAccordingly(-movement, -movement);
				this.setWidthHeight(w - movement, h - movement);
			}
		}
	}

	moveChildrenAccordingly(movementX: number, movementY: number) {
		for (const child of this.children) {
			child.move(movementX, movementY);
		}
	}

	resizeChildrenAccordingly(movementX: number, movementY: number) {
		for (const child of this.children) {
			child.resize(movementX, movementY);
		}
	}

	containsChildWithType(type: string) {
		return this.children.some((child) => child.getType() === type);
	}

	getChildByType<T extends CanvasObjectInterface<any>>(
		type: string | string[]
	): T | undefined {
		return (this.children as T[]).find((child) =>
			(Array.isArray(type) ? type : [type]).includes(child.getType())
		);
	}

	setShouldBeRendered(shouldRender: boolean) {
		this.needToRender = shouldRender;
	}

	shouldBeRendered(): boolean {
		return this.needToRender;
	}
}
